import { useEffect } from 'react';
import { flushAppInsights, setAppInsightsUser, trackEvent } from '@/lib/applicationInsights';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import {
  createEngagedTimeTracker,
  ENGAGED_FLUSH_INTERVAL_MS,
  ENGAGED_TIME_EVENT,
  pageFromRoute,
} from '@/lib/engagedTime';
import { router } from '@/src/router/routes';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'wheel'] as const;

function currentPage() {
  const { location, matches } = router.state;
  const leaf = matches[matches.length - 1];
  return pageFromRoute(leaf?.fullPath, location.pathname, location.search);
}

/**
 * Reports engaged time for the signed-in user. Mounted after `useAppInsights`
 * so the session's final sample is flushed before that hook clears the user.
 */
export function useEngagedTime() {
  const { user, isLoading } = useAuth();
  const email = user?.email;

  useEffect(() => {
    if (isLoading || !email) return;

    const tracker = createEngagedTimeTracker({
      now: () => Date.now(),
      onSample: (sample) => {
        // Re-assert the user so a sample flushed during sign-out is not anonymous.
        setAppInsightsUser(email);
        trackEvent(
          ENGAGED_TIME_EVENT,
          { page: sample.page, pathname: sample.pathname },
          { seconds: sample.seconds },
        );
      },
    });

    const onActivity = () => tracker.noteActivity();
    const onVisibility = () => tracker.setVisible(document.visibilityState === 'visible');
    const onPageHide = () => {
      tracker.setVisible(false);
      flushAppInsights();
    };
    // A back-forward cache restore fires pageshow, not visibilitychange, and
    // pagehide has already paused the tracker. Activity cannot resume it while
    // it stays marked hidden.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      tracker.setVisible(document.visibilityState === 'visible');
    };

    tracker.start(currentPage(), document.visibilityState === 'visible');

    const unsubscribe = router.subscribe('onResolved', () => {
      tracker.setPage(currentPage());
    });
    const interval = window.setInterval(() => tracker.checkpoint(), ENGAGED_FLUSH_INTERVAL_MS);

    for (const eventName of ACTIVITY_EVENTS) {
      document.addEventListener(eventName, onActivity, { capture: true, passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.clearInterval(interval);
      unsubscribe();
      for (const eventName of ACTIVITY_EVENTS) {
        document.removeEventListener(eventName, onActivity, { capture: true });
      }
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      tracker.stop();
      flushAppInsights();
    };
  }, [isLoading, email]);
}
